import { coordOf, isRoad, tracePath } from "./board.js";
import { isCore } from "./constants.js";
import { buildTowers } from "./towers.js";
import type { BattleResult, Layout, Tile } from "./types.js";

/**
 * Replay fog of war (D-028, 02 GDD §7.3).
 *
 * The defender's copy of a replay is always complete. The attacker's is only
 * complete if they broke through. Otherwise they see the road they actually
 * walked and the towers that did something to them — and nothing else.
 *
 * This is the mechanic that turns a failed attack into a scouting investment:
 * the attacker peels the maze back a layer at a time, while a defender who
 * stops them early gives almost nothing away. It also means the full layout is
 * never sent to the attacker's device, so it cannot be extracted from the
 * client.
 */

/** A layout with the parts the viewer has not earned replaced by null. */
export interface FogView {
  readonly size: number;
  /** The attacker always knows this: they chose where to enter. */
  readonly gate: { readonly x: number; readonly y: number };
  readonly wallLevel: number;
  /** Row-major. `null` means fog. */
  readonly tiles: readonly (Tile | null)[];
  /** How many tiles are visible, for the client's visibility mask. */
  readonly revealedTiles: number;
  /** How much of the defence is still unknown — the thing left to scout. */
  readonly unknownCount: number;
}

/**
 * Filter a layout for the attacker.
 *
 * Takes the result rather than the live battle state so the server can fog a
 * stored replay months later without re-running anything.
 */
export function applyFog(layout: Layout, result: BattleResult): FogView {
  // "Attack succeeded: the replay shows the complete defence — every road tile,
  // every tower with type and level, gate and core." (02 GDD §7.3)
  if (result.outcome === "breach") return fullView(layout);

  const visible = new Set<number>();

  // The core sits at a fixed, known position; hiding it reveals nothing.
  for (let i = 0; i < layout.tiles.length; i++) {
    const at = coordOf(i, layout.size);
    if (isCore(at.x, at.y)) visible.add(i);
  }

  const traced = tracePath(layout);
  if (traced.ok) {
    const walked = result.reveal.roadTilesWalked;
    for (let step = 0; step < walked && step < traced.path.length; step++) {
      const at = traced.path[step]!;
      visible.add(at.y * layout.size + at.x);
    }
  }

  // Towers that fired, were hit by a Ram, or were disabled by a Saboteur. A
  // tower that never did anything was never seen, so it stays hidden.
  const towers = buildTowers(layout);
  for (const id of result.reveal.towerIds) {
    const tower = towers[id];
    if (tower) visible.add(tower.at.y * layout.size + tower.at.x);
  }

  const tiles: (Tile | null)[] = layout.tiles.map((tile, i) => (visible.has(i) ? tile : null));

  // "Unknown" counts only what there is left to discover — empty ground is not
  // a secret, and telling the attacker how much empty ground exists would leak
  // the size of the maze.
  let unknownCount = 0;
  for (let i = 0; i < layout.tiles.length; i++) {
    if (visible.has(i)) continue;
    const kind = layout.tiles[i]?.kind;
    if (isRoad(kind) || kind === "tower_slot") unknownCount += 1;
  }

  return {
    size: layout.size,
    gate: layout.gate,
    wallLevel: layout.wallLevel,
    tiles,
    revealedTiles: visible.size,
    unknownCount,
  };
}

/**
 * The defender's view: everything, always. Present as its own function so the
 * call site says which viewer it is building for.
 */
export function fullView(layout: Layout): FogView {
  return {
    size: layout.size,
    gate: layout.gate,
    wallLevel: layout.wallLevel,
    tiles: [...layout.tiles],
    revealedTiles: layout.tiles.length,
    unknownCount: 0,
  };
}
